import React, { useEffect, useState } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useCanvaAuth } from '../contexts/CanvaAuthContext2.tsx';
import { useCanvaEmbedDocument } from '../hooks/useCanvaEmbedDocument.ts';

const CanvaRoute = ({ children }: { children: React.ReactNode }) => {
  const { canvaAuthenticated, setCanvaAuthenticated, setToken } = useCanvaAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();

  useCanvaEmbedDocument();

	const LOGIN_URL_FIXED = process.env.REACT_APP_LOGIN_URL;
	const RETRIEVE_URL_FIXED = process.env.REACT_APP_RETRIEVE_URL;

  const loginCanvaUser = async (sessionDetails) => {
    try {
      if (!LOGIN_URL_FIXED) {
        return null;
      }
      
      let jsonData = JSON.stringify({ locale: sessionDetails.locale });
      
      const response = await fetch(LOGIN_URL_FIXED, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionDetails.token}`,
          'Content-Type': 'application/json',
        },
        body: jsonData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return null;
    }
  };

  const retrieveSession = async (session) => {
    try {
      if (!RETRIEVE_URL_FIXED) {
        return null;
      }
      
      const url = `${RETRIEVE_URL_FIXED}?session=${session}`;
      const tokenResponse = await fetch(url);
      
      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        throw new Error(`HTTP error! status: ${tokenResponse.status}, message: ${errorText}`);
      }

      const data = await tokenResponse.json();
      return data;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const login = async () => {
      const session = searchParams.get('session');
      
      if (!session) {
        setCanvaAuthenticated(null);
        setIsLoading(false);
        return;
      }
      
      const sessionDetails = await retrieveSession(session);
      if (!sessionDetails || !sessionDetails.token) {
        setCanvaAuthenticated(null);
        setIsLoading(false);
        return;
      }

      const token = sessionDetails.token;
      const authData = await loginCanvaUser(sessionDetails);
      if (!authData) {
        setCanvaAuthenticated(null);
        setIsLoading(false);
        return;
      }
      
      setCanvaAuthenticated(authData);
      setToken(token);
      setIsLoading(false);
    };

    login();
  }, [searchParams, setCanvaAuthenticated, setToken]);

  if (isLoading) {
    return (
      <div className="canva-purchase-page canva-route-loading">
        <div className="canva-route-loading-inner d-flex justify-content-center align-items-center">
          <Spinner
            animation="border"
            role="status"
            className="canva-route-loading-spinner"
          >
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  return canvaAuthenticated ? <>{children}</> : <Navigate to="/canva-back?error=12" />;
};

export default CanvaRoute;
